import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '@material-ui/core/styles';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, TextField, Button, Grid, FormControlLabel, Switch,
  Box, makeStyles, CircularProgress, MenuItem, Select, FormControl,
  InputLabel, IconButton, Paper, Divider
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SaveIcon from '@material-ui/icons/Save';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import DateRangeIcon from '@material-ui/icons/DateRange';
import TuneIcon from '@material-ui/icons/Tune';
import LanguageIcon from '@material-ui/icons/Language';

import ImageUploadBox from '../../components/ImageUploadBox';
import { getCampaignById, createCampaign, updateCampaign } from '../../services/campaignService';

const useStyles = makeStyles((theme) => ({
  dialogRoot: {
    '& .MuiDialog-paper': {
      margin: 24,
      width: 'calc(100vw - 48px)',
      maxWidth: 'calc(100vw - 48px) !important',
      height: 'calc(100vh - 48px)',
      maxHeight: 'calc(100vh - 48px)',
      borderRadius: 20,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      background: theme.palette.type === 'dark' ? '#0f172a' : '#ffffff',
      border: `1px solid ${theme.palette.divider}`,
      boxShadow: '0 25px 70px rgba(0,0,0,0.35)',
      '@media (max-width: 600px)': {
        margin: 8,
        width: 'calc(100vw - 16px)',
        height: 'calc(100vh - 16px)',
        maxHeight: 'calc(100vh - 16px)',
        borderRadius: 14,
      },
    },
  },
  dialogHeader: {
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: theme.palette.type === 'dark' ? '#111827' : '#f8fafc',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  headerIcon: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: 'linear-gradient(135deg, rgba(16,185,129,0.2) 0%, rgba(5,150,105,0.2) 100%)',
    color: '#10b981',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: '1.2rem',
    color: theme.palette.text.primary,
    lineHeight: 1.2,
  },
  headerSubtitle: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  closeBtn: {
    color: theme.palette.text.secondary,
    borderRadius: 10,
    padding: 8,
    '&:hover': {
      backgroundColor: 'rgba(239,68,68,0.1)',
      color: '#ef4444',
    },
  },
  dialogContent: {
    padding: '24px 28px',
    overflowY: 'auto',
    flexGrow: 1,
    background: theme.palette.type === 'dark' ? '#0b1120' : '#f8fafc',
  },
  dialogActions: {
    padding: '14px 24px',
    borderTop: `1px solid ${theme.palette.divider}`,
    background: theme.palette.type === 'dark' ? '#111827' : '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  sectionCard: {
    padding: 24,
    borderRadius: 16,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    marginBottom: 20,
    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  sectionTitle: {
    fontWeight: 700,
    fontSize: '0.95rem',
    color: theme.palette.text.primary,
  },
  field: {
    marginBottom: theme.spacing(2.5),
    '& .MuiInputBase-root': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : '#ffffff',
      borderRadius: 10,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.secondary,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#10b981',
    },
  },
  selectControl: {
    marginBottom: theme.spacing(2.5),
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      fontSize: '0.9rem',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: '#10b981',
    },
    '& .MuiOutlinedInput-root': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(30,41,59,0.5)' : '#ffffff',
      borderRadius: 10,
      color: theme.palette.text.primary,
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.divider,
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.text.secondary,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#10b981',
    },
  },
  saveBtn: {
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#ffffff',
    fontWeight: 600,
    borderRadius: 10,
    padding: '8px 24px',
    boxShadow: '0 4px 14px rgba(16,185,129,0.3)',
    textTransform: 'none',
    '&:hover': {
      background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    },
  },
  cancelBtn: {
    borderRadius: 10,
    color: theme.palette.text.secondary,
    textTransform: 'none',
    fontWeight: 500,
    padding: '8px 20px',
  },
}));

export default function CampaignFormModal({ open, onClose, campaignId = null, onSuccess }) {
  const classes = useStyles();
  const theme = useTheme();

  const isEdit = Boolean(campaignId);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const [formData, setFormData] = useState({
    title_en: '',
    title_bn: '',
    body_en: '',
    body_bn: '',
    imageUrl: '',
    buttonText_en: 'Explore Now',
    buttonText_bn: 'এখনই দেখুন',
    actionUrl: '',
    startDate: '',
    endDate: '',
    durationDays: 7,
    displayFrequency: 'once_per_day',
    maxImpressions: 3,
    targetScreen: 'all',
    isActive: true,
  });

  const loadData = useCallback(async () => {
    if (!campaignId) {
      const today = new Date();
      const end = new Date();
      end.setDate(today.getDate() + 7);
      setFormData({
        title_en: '',
        title_bn: '',
        body_en: '',
        body_bn: '',
        imageUrl: '',
        buttonText_en: 'Explore Now',
        buttonText_bn: 'এখনই দেখুন',
        actionUrl: '',
        startDate: today.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0],
        durationDays: 7,
        displayFrequency: 'once_per_day',
        maxImpressions: 3,
        targetScreen: 'all',
        isActive: true,
      });
      return;
    }

    setLoading(true);
    const data = await getCampaignById(campaignId);
    if (data) {
      setFormData({
        title_en: data.title_en || data.title || '',
        title_bn: data.title_bn || '',
        body_en: data.body_en || data.body || '',
        body_bn: data.body_bn || '',
        imageUrl: data.imageUrl || '',
        buttonText_en: data.buttonText_en || 'Explore Now',
        buttonText_bn: data.buttonText_bn || 'এখনই দেখুন',
        actionUrl: data.actionUrl || '',
        startDate: data.startDate || '',
        endDate: data.endDate || '',
        durationDays: data.durationDays || 7,
        displayFrequency: data.displayFrequency || 'once_per_day',
        maxImpressions: data.maxImpressions || 3,
        targetScreen: data.targetScreen || 'all',
        isActive: data.isActive !== undefined ? data.isActive : true,
      });
    }
    setLoading(false);
  }, [campaignId]);

  useEffect(() => {
    if (open) {
      setErrorMsg('');
      loadData();
    }
  }, [open, loadData]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setFormData((prev) => {
      const updated = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };

      if (name === 'startDate' || name === 'durationDays') {
        const start = name === 'startDate' ? value : prev.startDate;
        const days = name === 'durationDays' ? parseInt(value, 10) : prev.durationDays;
        if (start && days > 0) {
          const d = new Date(start);
          d.setDate(d.getDate() + days);
          updated.endDate = d.toISOString().split('T')[0];
        }
      }

      if (name === 'endDate' && prev.startDate && value) {
        const diffTime = new Date(value) - new Date(prev.startDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        if (diffDays > 0) updated.durationDays = diffDays;
      }

      return updated;
    });
  };

  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();

    if (!formData.title_en.trim() || !formData.body_en.trim()) {
      setErrorMsg('English Title and English Body are required.');
      return;
    }

    setSaving(true);
    setErrorMsg('');
    try {
      const payload = {
        ...formData,
        title: formData.title_en,
        body: formData.body_en,
        durationDays: parseInt(formData.durationDays, 10) || 7,
        maxImpressions: parseInt(formData.maxImpressions, 10) || 3,
      };

      if (isEdit) {
        await updateCampaign(campaignId, payload);
      } else {
        await createCampaign(payload);
      }

      setSaving(false);
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
      setErrorMsg(err?.message || 'Failed to save campaign.');
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      className={classes.dialogRoot}
      disableBackdropClick={saving}
      disableEscapeKeyDown={saving}
      scroll="paper"
    >
      <DialogTitle disableTypography className={classes.dialogHeader}>
        <div className={classes.headerLeft}>
          <div className={classes.headerIcon}>
            <AnnouncementIcon />
          </div>
          <div>
            <Typography className={classes.headerTitle}>
              {isEdit ? 'ক্যাম্পেইন সম্পাদনা (Edit In-App Campaign)' : 'নতুন ইন-অ্যাপ ক্যাম্পেইন (Create In-App Campaign)'}
            </Typography>
            <Typography className={classes.headerSubtitle}>
              {isEdit ? `ID: ${campaignId} • ${formData.title_en || 'Campaign'}` : 'ইউজারদের ডিভাইসে ইন-অ্যাপ পপআপ/মেসেজ ব্যানার তৈরি করুন'}
            </Typography>
          </div>
        </div>

        <IconButton
          onClick={onClose}
          className={classes.closeBtn}
          size="small"
          disabled={saving}
          title="Close Modal"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent className={classes.dialogContent} dividers>
        {loading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={300}>
            <CircularProgress style={{ color: '#10b981' }} size={48} thickness={4} />
            <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
              ক্যাম্পেইনের তথ্য লোড হচ্ছে...
            </Typography>
          </Box>
        ) : (
          <>
            {errorMsg && (
              <Box mb={2.5} p={1.5} borderRadius={8} bgcolor="rgba(239,68,68,0.1)" border="1px solid rgba(239,68,68,0.3)">
                <Typography variant="body2" style={{ color: '#ef4444', fontWeight: 600 }}>
                  <span role="img" aria-label="error">⚠️</span> {errorMsg}
                </Typography>
              </Box>
            )}

            {/* Content & Messages */}
            <Paper elevation={0} className={classes.sectionCard}>
              <div className={classes.sectionHeader}>
                <LanguageIcon style={{ color: '#10b981' }} />
                <Typography className={classes.sectionTitle}>
                  ক্যাম্পেইন শিরোনাম ও বার্তা (Title &amp; Message Body)
                </Typography>
              </div>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Title (English) *"
                    name="title_en"
                    value={formData.title_en}
                    onChange={handleChange}
                    variant="outlined"
                    className={classes.field}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="শিরোনাম (বাংলা)"
                    name="title_bn"
                    value={formData.title_bn}
                    onChange={handleChange}
                    variant="outlined"
                    className={classes.field}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Message Body (English) *"
                    name="body_en"
                    value={formData.body_en}
                    onChange={handleChange}
                    variant="outlined"
                    className={classes.field}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="বিস্তারিত বার্তা (বাংলা)"
                    name="body_bn"
                    value={formData.body_bn}
                    onChange={handleChange}
                    variant="outlined"
                    className={classes.field}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Media & Action */}
            <Paper elevation={0} className={classes.sectionCard}>
              <Typography className={classes.sectionTitle} style={{ marginBottom: 16 }}>
                ছবি ও অ্যাকশন বাটন (Image &amp; Action Button)
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <ImageUploadBox
                    value={formData.imageUrl}
                    onChange={(url) => setFormData((prev) => ({ ...prev, imageUrl: url }))}
                    label="Campaign Banner / Image (16:9 Recommended)"
                    folder="campaigns"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Button Text (English)"
                    name="buttonText_en"
                    value={formData.buttonText_en}
                    onChange={handleChange}
                    variant="outlined"
                    className={classes.field}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="বাটন টেক্সট (বাংলা)"
                    name="buttonText_bn"
                    value={formData.buttonText_bn}
                    onChange={handleChange}
                    variant="outlined"
                    className={classes.field}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Action Link / Target URL (optional)"
                    placeholder="https://... or app://screen"
                    name="actionUrl"
                    value={formData.actionUrl}
                    onChange={handleChange}
                    variant="outlined"
                    className={classes.field}
                    helperText="URL to open when the user clicks the button."
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Schedule & Rules */}
            <Paper elevation={0} className={classes.sectionCard}>
              <div className={classes.sectionHeader}>
                <DateRangeIcon style={{ color: '#10b981' }} />
                <Typography className={classes.sectionTitle}>
                  Schedule &amp; Duration (সময়সূচী)
                </Typography>
              </div>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Start Date"
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleChange}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    className={classes.field}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Duration (Days)"
                    name="durationDays"
                    value={formData.durationDays}
                    onChange={handleChange}
                    variant="outlined"
                    inputProps={{ min: 1, max: 365 }}
                    className={classes.field}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="date"
                    label="End Date"
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleChange}
                    variant="outlined"
                    InputLabelProps={{ shrink: true }}
                    className={classes.field}
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Frequency & Display Settings */}
            <Paper elevation={0} className={classes.sectionCard}>
              <div className={classes.sectionHeader}>
                <TuneIcon style={{ color: '#10b981' }} />
                <Typography className={classes.sectionTitle}>
                  প্রদর্শন ও ফ্রিকোয়েন্সি সেটিংস (Delivery Rules)
                </Typography>
              </div>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth variant="outlined" className={classes.selectControl}>
                    <InputLabel id="frequency-label">Display Frequency</InputLabel>
                    <Select
                      labelId="frequency-label"
                      name="displayFrequency"
                      value={formData.displayFrequency}
                      onChange={handleChange}
                      label="Display Frequency"
                    >
                      <MenuItem value="once_per_day">Once per Day</MenuItem>
                      <MenuItem value="once_per_session">Once per App Open</MenuItem>
                      <MenuItem value="always">Always (Every matching screen visit)</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Max Impressions per User"
                    name="maxImpressions"
                    value={formData.maxImpressions}
                    onChange={handleChange}
                    variant="outlined"
                    inputProps={{ min: 1, max: 99 }}
                    className={classes.field}
                    helperText="Total times a user will see this."
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth variant="outlined" className={classes.selectControl}>
                    <InputLabel id="screen-label">Target Screen</InputLabel>
                    <Select
                      labelId="screen-label"
                      name="targetScreen"
                      value={formData.targetScreen}
                      onChange={handleChange}
                      label="Target Screen"
                    >
                      <MenuItem value="all">Any Screen (Global)</MenuItem>
                      <MenuItem value="home">Home Screen Only</MenuItem>
                      <MenuItem value="quran">Quran Screen</MenuItem>
                      <MenuItem value="hadith">Hadith Screen</MenuItem>
                      <MenuItem value="duas">Dua Screen</MenuItem>
                      <MenuItem value="tasbeeh">Tasbeeh Screen</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        <FormControlLabel
          control={
            <Switch
              checked={formData.isActive}
              onChange={handleChange}
              name="isActive"
              color="primary"
            />
          }
          label={
            <Typography variant="body2" style={{ fontWeight: 600, color: theme.palette.text.primary }}>
              {formData.isActive ? 'ক্যাম্পেইন সক্রিয় (Active)' : 'নিষ্ক্রিয় (Inactive)'}
            </Typography>
          }
        />

        <Box display="flex" alignItems="center" style={{ gap: 16 }}>
          <Button onClick={onClose} className={classes.cancelBtn} disabled={saving} style={{ marginRight: 8 }}>
            Cancel
          </Button>

          <Button
            variant="contained"
            className={classes.saveBtn}
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving || loading}
          >
            {saving ? 'সংরক্ষণ হচ্ছে...' : isEdit ? 'আপডেট করুন (Save Changes)' : 'ক্যাম্পেইন প্রকাশ করুন (Publish Campaign)'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
